import type { TeamBrand } from '@/types';

export interface BrandOption {
  id: number;
  label: string;
  isPrimary: boolean;
}

/** Anything with a brand list — a full `TeamDetail` or just the brands-only read model. */
export interface TeamBrandsSource {
  brands: TeamBrand[];
}

function sortByLabel(options: BrandOption[]): BrandOption[] {
  return options.sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Brand dropdown options for the global filter bar.
 *
 * Rule: offer every brand from every team the user belongs to (union,
 * de-duplicated) — a primary brand only affects the *default* selection
 * (see `pickDefaultBrandId`), it never restricts which brands are choosable.
 * A brand is marked primary if any contributing team has it as its primary.
 */
export function deriveBrandOptions(teams: TeamBrandsSource[]): BrandOption[] {
  const byId = new Map<number, BrandOption>();
  for (const team of teams) {
    for (const b of team.brands) {
      const existing = byId.get(b.brandId);
      if (existing) {
        if (b.isPrimary) existing.isPrimary = true;
      } else {
        byId.set(b.brandId, { id: b.brandId, label: b.name, isPrimary: b.isPrimary });
      }
    }
  }
  return sortByLabel(Array.from(byId.values()));
}

/**
 * Default brand selection for a first-time picker: the first primary brand
 * by alphabetical order, or (when the user has no primary brand at all) the
 * first brand in the list, matching prior behavior.
 */
export function pickDefaultBrandId(options: BrandOption[]): number | undefined {
  if (options.length === 0) return undefined;
  const primary = options.find((o) => o.isPrimary);
  return (primary ?? options[0]).id;
}

/**
 * Competitor dropdown options for a selected brand: every *other* brand from
 * each team in which the selected brand appears. Returns [] when no brand is
 * selected or the brand is not part of any known team.
 */
export function deriveCompetitorOptions(
  teams: TeamBrandsSource[],
  selectedBrandId: number | undefined,
): BrandOption[] {
  if (selectedBrandId === undefined) return [];
  const byId = new Map<number, BrandOption>();
  for (const team of teams) {
    if (!team.brands.some((b) => b.brandId === selectedBrandId)) continue;
    for (const b of team.brands) {
      if (b.brandId === selectedBrandId) continue;
      if (!byId.has(b.brandId))
        byId.set(b.brandId, { id: b.brandId, label: b.name, isPrimary: b.isPrimary });
    }
  }
  return sortByLabel(Array.from(byId.values()));
}
